import { useEffect } from 'react';

/**
 * Scrollbar genişliğini hesaplar
 */
const getScrollbarWidth = (): number => {
  // Eğer scrollbar yoksa 0 döndür
  if (typeof window === 'undefined') return 0;

  // Geçici div oluştur
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll';
  // IE and Edge için CSS property'si (TypeScript uyumluluğu için any cast)
  (outer.style as any).msOverflowStyle = 'scrollbar';
  document.body.appendChild(outer);

  // İç div oluştur
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Scrollbar genişliğini hesapla
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  // Temizlik
  outer.parentNode?.removeChild(outer);

  return scrollbarWidth;
};

// Global modal counter - kaç modal açık olduğunu takip eder
let modalCount = 0;



/**
 * Modal ve sheet componentler için scroll engelleyici hook
 * 
 * Mobil'de (768px altı): Scrollbar gizlenir ve sayfa sabitlener (position: fixed)
 * 
 * @param isOpen - Modal/sheet açık mı?
 */
export const useScrollPrevention = (isOpen: boolean) => {
  useEffect(() => {
    if (!isOpen) return;

    // Modal sayacını artır
    modalCount++;
    const isFirstModal = modalCount === 1;



    // Modal açık durumunu body'ye işaretle (BackToTop için)
    document.body.setAttribute('data-modal-open', 'true');

    // Sadece ilk modal body stillerini yönetsin
    if (!isFirstModal) {
      // Üst modal sadece cleanup fonksiyonu döndürür
      return () => {
        modalCount--;


        // Bir sonraki frame'de kontrol et (modal geçişleri için)
        requestAnimationFrame(() => {
          if (modalCount === 0) {

            // Modal kapanış animasyonu için gecikme (300ms)
            setTimeout(() => {
              document.body.style.removeProperty('margin-right');
              document.body.style.removeProperty('margin-left');
              document.body.removeAttribute('data-modal-open');
            }, 300);
          }
        });
      };
    }



    if (isFirstModal) {
      const scrollbarWidth = getScrollbarWidth();
      const hasVerticalScrollbar = document.documentElement.scrollHeight > window.innerHeight;

      // Sadece dikey scrollbar varsa CSS variable set et (fixed elementler için)
      if (hasVerticalScrollbar && scrollbarWidth > 0) {
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);

        // Modal açıldıktan sonra margin-left'i scrollbar genişliği kadar set et
        requestAnimationFrame(() => {
          if (window.innerWidth > 820) {
            document.body.style.marginLeft = `${scrollbarWidth - 4}px`;

          }
          document.body.style.marginRight = `${scrollbarWidth}px`;
        });
      }

      // CSS ile scroll engelleme için sadece body'ye data attribute ekle
    }

    // Scroll engelleyici event listener'lar
    const preventScroll = (e: Event) => {
      const target = e.target as Element;

      // Modal içi scrollable elementleri kontrol et
      const isInsideScrollableModal = target.closest('.modal-scrollable') ||
        target.closest('[data-modal-scrollable]') ||
        target.closest('.overflow-y-auto');

      // Eğer scrollable modal içerisindeyse scroll'a izin ver
      if (isInsideScrollableModal) {
        return;
      }

      // Diğer durumlarda scroll'u engelle
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as Element;
      const isInsideScrollableModal = target.closest('.modal-scrollable') ||
        target.closest('[data-modal-scrollable]') ||
        target.closest('.overflow-y-auto');

      if (isInsideScrollableModal) {
        return;
      }

      // Input, textarea ve contenteditable elementlerde boşluk karakterine izin ver
      const isInputElement = target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        (target as HTMLElement).contentEditable === 'true';

      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key)) {
        e.preventDefault();
      } else if (e.key === ' ' && !isInputElement) {
        e.preventDefault();
      }
    };

    // Event listener'ları sadece ilk modal eklesin
    if (isFirstModal) {
      document.addEventListener('wheel', preventScroll, { passive: false });
      document.addEventListener('touchmove', preventScroll, { passive: false });
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      modalCount--;

      // Event listener'ları hemen temizle (sadece ilk modal eklediği için)
      if (isFirstModal) {
        document.removeEventListener('wheel', preventScroll);
        document.removeEventListener('touchmove', preventScroll);
        document.removeEventListener('keydown', handleKeyDown);
      }

      // Bir sonraki frame'de kontrol et (modal geçişleri için)
      requestAnimationFrame(() => {
        if (modalCount === 0) {
          // Modal kapanış animasyonu için gecikme (300ms)
          setTimeout(() => {
            document.documentElement.style.removeProperty('--scrollbar-width');
            document.body.style.removeProperty('margin-right');
            document.body.style.removeProperty('margin-left');
            document.body.removeAttribute('data-modal-open');
          }, 300);
        }
      });
    };
  }, [isOpen]);
};
